import React, { lazy, Suspense, useEffect, useState } from 'react';
import axios from 'axios';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Home from './pages/Home';
import { BrandSplash } from './components/BrandSplash';
import { PlantsProvider } from './contexts/PlantsContext';
import { MasterDataProvider } from './contexts/MasterDataContext';
import { Toaster } from './components/ui/sonner';
import { ProtectedRoute } from './components/ProtectedRoute';
import Layout from './components/Layout';
import Login from './pages/Login';
import Register from './pages/Register';
import SwamiSSOShell from './components/SwamiSSOShell';
import './App.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ─── Auto-rewrite stale baked-in URLs to current page origin ───
// Problem: when the operator changes the production domain (e.g. moves to
// `swami-oee.in`), any pre-built frontend bundle still has the OLD backend URL
// baked in via `process.env.REACT_APP_BACKEND_URL`. Result: every API call hits
// the old (possibly unreachable or CORS-restricted) host → "Something went wrong".
//
// This interceptor inspects every outgoing axios URL. If it points to a host that
// doesn't match the current browser origin, we rewrite it to the current origin —
// so same-origin /api calls always work without needing a rebuild.
if (typeof window !== 'undefined' && window.location && axios?.interceptors) {
  axios.interceptors.request.use((config) => {
    try {
      const url = config?.url;
      if (!url) return config;
      // Only rewrite absolute URLs, not already-relative ones
      if (!/^https?:\/\//i.test(url)) return config;
      const target = new URL(url);
      const cur = new URL(window.location.origin);
      if (target.host !== cur.host) {
        // Preserve path + query, swap host with current origin
        config.url = cur.origin + target.pathname + target.search + target.hash;
      }
    } catch {
      /* ignore — leave URL untouched */
    }
    return config;
  });
}

// Lazy import with automatic recovery on stale chunks (post-deploy ChunkLoadError).
// When the browser still has an old session loaded after a redeploy, the chunk
// hashes have changed; fetching them returns 404 and React Suspense renders blank.
// We catch that, set a one-shot session flag, and hard-reload so the user picks up
// the fresh `index.html` with the new chunk references.
function lazyWithRetry(importer) {
  return lazy(async () => {
    try {
      return await importer();
    } catch (err) {
      const msg = String(err?.message || err);
      const isChunkError =
        err?.name === 'ChunkLoadError' ||
        /Loading chunk [\d]+ failed/.test(msg) ||
        /Loading CSS chunk [\d]+ failed/.test(msg) ||
        /Failed to fetch dynamically imported module/i.test(msg) ||
        /error loading dynamically imported module/i.test(msg);
      if (isChunkError && typeof window !== 'undefined') {
        const flag = 'app:chunk-reload';
        if (!window.sessionStorage.getItem(flag)) {
          window.sessionStorage.setItem(flag, '1');
          window.location.reload();
          // Return a placeholder while reload happens
          return { default: () => null };
        }
      }
      throw err;
    }
  });
}

const Dashboard = lazyWithRetry(() => import('./pages/Dashboard'));
const LiveDashboard = lazyWithRetry(() => import('./pages/LiveDashboard'));
const AutoJobCard = lazyWithRetry(() => import('./pages/AutoJobCard'));
const RpiEdgeDiagnostics = lazyWithRetry(() => import('./pages/RpiEdgeDiagnostics'));
const ProductionSheet = lazyWithRetry(() => import('./pages/ProductionSheet'));
const ProductionLogs = lazyWithRetry(() => import('./pages/ProductionLogs'));
const OEEAnalysis = lazyWithRetry(() => import('./pages/OEEAnalysis'));
const FinalInspection = lazyWithRetry(() => import('./pages/FinalInspection'));
const ItemMaster = lazyWithRetry(() => import('./pages/ItemMaster'));
const ContractorMaster = lazyWithRetry(() => import('./pages/ContractorMaster'));
const MachineMaster = lazyWithRetry(() => import('./pages/MachineMaster'));
const Settings = lazyWithRetry(() => import('./pages/Settings'));
const Graphs = lazyWithRetry(() => import('./pages/Graphs'));
const HR = lazyWithRetry(() => import('./pages/HR'));
const ProblemList = lazyWithRetry(() => import('./pages/ProblemList'));
const DefectMaster = lazyWithRetry(() => import('./pages/DefectMaster'));
const MasterPanel = lazyWithRetry(() => import('./pages/MasterPanel'));
const SetupLog = lazyWithRetry(() => import('./pages/SetupLog'));
const BreakdownLog = lazyWithRetry(() => import('./pages/BreakdownLog'));
const ToolLog = lazyWithRetry(() => import('./pages/ToolLog'));
const Tracker4W1H = lazyWithRetry(() => import('./pages/Tracker4W1H'));
const CapacitySheet = lazyWithRetry(() => import('./pages/CapacitySheet'));
const Schedule = lazyWithRetry(() => import('./pages/Schedule'));
const ScheduleBooking = lazyWithRetry(() => import('./pages/ScheduleBooking'));
const CompanySetup = lazyWithRetry(() => import('./pages/CompanySetup'));
const QualityDashboard = lazyWithRetry(() => import('./pages/quality/QualityDashboard'));
const QaInspections = lazyWithRetry(() => import('./pages/quality/QaInspections'));
const QaApprovals = lazyWithRetry(() => import('./pages/quality/QaApprovals'));
const DocumentMaster = lazyWithRetry(() => import('./pages/quality/DocumentMaster'));
const DriveSettings = lazyWithRetry(() => import('./pages/quality/DriveSettings'));

function PageLoader() {
  return <div className="flex items-center justify-center py-20"><div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-r-transparent"></div></div>;
}

// Defensive backstop: if the lazy() retry above doesn't catch the chunk error
// (e.g., it bubbled up from a deeper dynamic import), force-reload silently.
//
// For NON-chunk errors we must NEVER silently render `null` — that gives the
// user a blank white screen with no recovery path. Instead, surface a clear
// error UI with a Reload button so the operator isn't stranded.
class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { errored: false, isChunk: false, errorMsg: '' };
  }
  static getDerivedStateFromError(err) {
    const msg = String(err?.message || err);
    const isChunkError =
      err?.name === 'ChunkLoadError' ||
      /Loading chunk [\d]+ failed/.test(msg) ||
      /Loading CSS chunk [\d]+ failed/.test(msg) ||
      /Failed to fetch dynamically imported module/i.test(msg) ||
      /error loading dynamically imported module/i.test(msg);
    if (isChunkError && typeof window !== 'undefined') {
      const flag = 'app:chunk-reload';
      if (!window.sessionStorage.getItem(flag)) {
        window.sessionStorage.setItem(flag, '1');
        window.location.reload();
      }
    }
    return { errored: true, isChunk: isChunkError, errorMsg: msg };
  }
  componentDidCatch(err, info) {
    // Surface in console so operators can share a screenshot during support.
    try { console.error('[RouteErrorBoundary]', err, info?.componentStack); } catch { /* ignore */ }
  }
  render() {
    if (this.state.errored) {
      // Chunk errors trigger a reload above — render nothing while it happens.
      if (this.state.isChunk) return null;
      // Non-chunk errors: show a visible fallback so the user isn't stranded
      // on a blank screen. They can reload to recover.
      return (
        <div className="min-h-[60vh] flex items-center justify-center p-6">
          <div className="max-w-md w-full bg-white border border-red-200 rounded-lg p-6 text-center shadow-sm">
            <p className="text-sm font-black text-red-700 uppercase tracking-wider mb-2">Something went wrong</p>
            <p className="text-xs text-slate-600 mb-4">
              The page hit an unexpected error. Your data is safe — click reload to recover.
            </p>
            <pre className="text-[10px] text-slate-500 bg-slate-50 border border-slate-200 rounded p-2 mb-4 text-left overflow-x-auto whitespace-pre-wrap">
              {this.state.errorMsg.slice(0, 400)}
            </pre>
            <button
              onClick={() => { try { window.sessionStorage.removeItem('app:chunk-reload'); } catch { /* ignore */ } ; window.location.reload(); }}
              className="px-4 py-2 text-xs font-bold uppercase tracking-wider bg-blue-600 hover:bg-blue-700 text-white rounded">
              Reload Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function AppRoutes() {
  const { user, loading } = useAuth();
  const isMaster = user?.role === 'master';
  // Only SSO-onboarded tenants see the Setup wizard.
  // Regular username/password logins skip setup and go straight to the dashboard —
  // their company record is already created at registration time.
  const needsSetup = !!user && user.role !== 'master' && user.setup_complete === false && user.is_sso === true;

  // Clear the chunk-reload guard once we've successfully mounted on the new chunks,
  // so a future redeploy can trigger the auto-recovery again.
  useEffect(() => {
    try { window.sessionStorage.removeItem('app:chunk-reload'); } catch { /* ignore */ }
  }, []);

  if (loading) {
    return <BrandSplash />;
  }

  return (
    <RouteErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/login" element={user ? <Navigate to={isMaster ? "/master" : (needsSetup ? "/setup" : "/dashboard")} replace /> : <Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/setup" element={user && !isMaster ? <CompanySetup /> : <Navigate to={user ? (isMaster ? "/master" : "/dashboard") : "/login"} replace />} />

          <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
            {isMaster ? (
              <>
                <Route path="/master" element={<MasterPanel />} />
                <Route path="*" element={<Navigate to="/master" replace />} />
              </>
            ) : needsSetup ? (
              <Route path="*" element={<Navigate to="/setup" replace />} />
            ) : (
              <>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/live-dashboard" element={<LiveDashboard />} />
                <Route path="/auto-jobcard" element={<AutoJobCard />} />
                <Route path="/rpi-edge" element={<RpiEdgeDiagnostics />} />
                <Route path="/production-sheet" element={<ProductionSheet />} />
                <Route path="/production-logs" element={<ProductionLogs />} />
                <Route path="/oee-analysis" element={<OEEAnalysis />} />
                <Route path="/final-inspection" element={<FinalInspection />} />
                <Route path="/graphs" element={<Graphs />} />
                <Route path="/hr" element={<HR />} />
                <Route path="/problem-list" element={<ProblemList />} />
                <Route path="/defect-master" element={<DefectMaster />} />
                <Route path="/item-master" element={<ItemMaster />} />
                <Route path="/operator-master" element={<Navigate to="/hr" replace />} />
                <Route path="/contractor-master" element={<ContractorMaster />} />
                <Route path="/machine-master" element={<MachineMaster />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/setup-log" element={<SetupLog />} />
                <Route path="/breakdown-log" element={<BreakdownLog />} />
                <Route path="/tool-log" element={<ToolLog />} />
                <Route path="/tracker" element={<Tracker4W1H />} />
                <Route path="/capacity-sheet" element={<CapacitySheet />} />
                <Route path="/schedule" element={<Schedule />} />
                <Route path="/schedule-booking" element={<ScheduleBooking />} />
                <Route path="/quality/dashboard" element={<QualityDashboard />} />
                <Route path="/quality/inspections" element={<QaInspections />} />
                <Route path="/quality/approvals" element={<QaApprovals />} />
                <Route path="/quality/documents" element={<DocumentMaster />} />
                <Route path="/quality/drive" element={<DriveSettings />} />
              </>
            )}
          </Route>

          <Route path="/" element={user ? <Navigate to={isMaster ? "/master" : (needsSetup ? "/setup" : "/dashboard")} replace /> : <Home />} />
          <Route path="*" element={<Navigate to={user ? (isMaster ? "/master" : (needsSetup ? "/setup" : "/dashboard")) : "/login"} replace />} />
        </Routes>
      </Suspense>
    </RouteErrorBoundary>
  );
}

function PublicSetupGate({ children }) {
  const [ready, setReady] = useState(false);
  const [publicNeedsSetup, setPublicNeedsSetup] = useState(false);

  // If the URL carries a Swami Hub SSO token, ALWAYS skip the public-setup gate.
  // The SSO exchange endpoint creates/links the company itself, so showing the
  // public wizard would just strip the one-time token and leave the user stranded
  // on a blank/looping screen. Detected at module-load so the very first paint
  // already renders SwamiSSOShell.
  const hasSsoToken = (() => {
    try { return new URL(window.location.href).searchParams.has('sso_token'); }
    catch { return false; }
  })();

  useEffect(() => {
    if (hasSsoToken) { setReady(true); return; }
    axios.get(`${API_URL}/api/company/public-status`)
      .then(r => setPublicNeedsSetup(!!r.data?.needs_setup))
      .catch(() => { /* treat network errors as "no public setup needed" to avoid locking out */ })
      .finally(() => setReady(true));
  }, [hasSsoToken]);

  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-r-transparent"></div>
      </div>
    );
  }

  if (publicNeedsSetup && !hasSsoToken) {
    // Fresh install — wizard BEFORE login. Every route resolves to /setup.
    return (
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/setup" element={
              <CompanySetup publicMode onComplete={() => {
                setPublicNeedsSetup(false);
                window.location.replace('/login');
              }} />
            } />
            <Route path="*" element={<Navigate to="/setup" replace />} />
          </Routes>
        </Suspense>
        <Toaster />
      </BrowserRouter>
    );
  }

  return children;
}

function App() {
  return (
    <PublicSetupGate>
      <SwamiSSOShell>
        <AuthProvider>
          <PlantsProvider>
            <MasterDataProvider>
              <BrowserRouter>
                <AppRoutes />
                <Toaster />
              </BrowserRouter>
            </MasterDataProvider>
          </PlantsProvider>
        </AuthProvider>
      </SwamiSSOShell>
    </PublicSetupGate>
  );
}

export default App;
